import React, { useEffect, useState } from 'react';
import LandingLayout from './components/landing/LandingLayout';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
// Import app's UI components instead of using PrivacyPolicyDialog directly
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Checkbox } from './components/ui/checkbox';
import { Label } from './components/ui/label';
import PrivacyPolicy from './PrivacyPolicy'; // Import the actual privacy policy content

const Splashscreen = (props) => {
  const auth = getAuth();
  const [user, setUser] = useState(auth.currentUser);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Check if we're dealing with an authenticated user who needs to give consent
  useEffect(() => {
    let isCancelled = false;
    const checkUserStatus = async () => {
      const currentUser = auth.currentUser;
      if (!isCancelled) {
        setUser(currentUser);
      }
      
      if (currentUser) {
        try {
          if (!isCancelled) {
            setLoading(true);
          }
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (isCancelled) return;
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Fail-safe: treat consentGiven as required unless explicitly 'true'.
            // A brand-new user's minimal identity document does not set consentGiven/
            // needsPrivacyConsent at all, so this must default to "needs consent"
            // rather than only matching an explicit 'false'/true value.
            const needsPrivacyConsent = userData.consentGiven !== 'true' || userData.needsPrivacyConsent === true;
            setNeedsConsent(needsPrivacyConsent);
            if (process.env.NODE_ENV === 'development') {
              console.log("Auth user needs consent:", needsPrivacyConsent);
            }
            
            // If user needs consent, trigger the privacy dialog open
            if (needsPrivacyConsent && props.onPrivacyDialogOpen) {
              props.onPrivacyDialogOpen(true);
            }
          } else {
            // Document genuinely doesn't exist yet (brand-new user, doc creation
            // still in flight). Default to requiring consent rather than silently
            // falling through to the login page — App.js's onSnapshot listener
            // will finish creating the profile and re-render this component.
            setNeedsConsent(true);
            if (props.onPrivacyDialogOpen) {
              props.onPrivacyDialogOpen(true);
            }
          }
        } catch (error) {
          if (!isCancelled) {
            console.error("Error checking user consent:", error);
          }
        } finally {
          if (!isCancelled) {
            setLoading(false);
          }
        }
      }
    };
    
    checkUserStatus();
    return () => { isCancelled = true; };
  }, [auth.currentUser, props.onPrivacyDialogOpen]);
  
  // If user is logged in and needs consent, show the privacy policy with app's styling
  if (user && needsConsent) {
    return (
      <div className="flex justify-center items-center min-h-screen p-4 bg-gray-100 dark:bg-[#1E1E2D]">
        <Card className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 max-w-3xl w-full max-h-[90vh] overflow-auto"> {/* Adjusted dark mode card border and bg */}
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Privacy Policy Consent</CardTitle> {/* Adjusted dark mode text */}
            <CardDescription className="text-gray-600 dark:text-gray-400"> {/* Adjusted dark mode text */}
              Please review and accept our Privacy Policy to continue using the application.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* The PrivacyPolicy component itself has styles that should adapt.
                The container below is for the scrollable area of the policy text.
                Its dark mode styles seem okay from previous context (dark:bg-muted/40 dark:text-popover-foreground)
                but let's ensure its parent Card's dark bg is distinct.
            */}
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300"> {/* Adjusted dark mode bg and text */}
              <PrivacyPolicy />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="consent" 
                checked={consentChecked} 
                onCheckedChange={setConsentChecked}
                // Light mode: gray border, purple check
                // Dark mode: lighter gray border, lighter purple check, white checkmark
                className="border-gray-400 data-[state=checked]:bg-purple-600 data-[state=checked]:text-white dark:border-gray-500 dark:data-[state=checked]:bg-purple-500 dark:data-[state=checked]:text-white"
              />
              <Label 
                htmlFor="consent"
                // Light mode: dark gray text
                // Dark mode: light gray text
                className="text-gray-800 dark:text-gray-200" 
              >
                I have read and agree to the Privacy Policy
              </Label>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              // Light mode: purple bg, white text
              // Dark mode: slightly lighter purple bg, white text
              className="w-full bg-purple-600 text-white hover:bg-purple-700 dark:bg-purple-500 dark:text-white dark:hover:bg-purple-600" 
              disabled={!consentChecked || loading}
              onClick={async () => {
                if (props.onPrivacyPolicyAccepted) {
                  setLoading(true);
                  await props.onPrivacyPolicyAccepted();
                  setLoading(false);
                }
              }}
            >
              {loading ? "Processing..." : "Accept & Continue"}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  // For non-authenticated users or those who've already given consent, use LandingLayout
  return <LandingLayout {...props} />;
};

export default Splashscreen;